import { createSlice, createSelector } from '@reduxjs/toolkit';

/** Recalculate a line item's amount from rate, qty, and discount. */
function calcAmount(item) {
  const gross = item.qty * item.rate;
  const discountAmt = (gross * (item.discount_percentage || 0)) / 100;
  item.amount = gross - discountAmt;
}

const initialState = {
  items: [],       // [{ item_code, item_name, qty, rate, amount, discount_percentage, note, uom, stock_uom, item_image }]
  customer: null,  // { name, customer_name } | null
  quote: null,     // server-authoritative cart totals/pricing response
  loyaltyRedemption: {
    enabled: false,
    points: 0,
    loyalty_program: null,
  },
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /**
     * Add an item to the cart.
     * If the item already exists (by item_code), increment its qty.
     */
    addItem(state, action) {
      const incoming = action.payload;
      const existing = state.items.find((i) => i.item_code === incoming.item_code);

      if (existing) {
        existing.qty += 1;
        calcAmount(existing);
      } else {
        const newItem = {
          item_code: incoming.item_code,
          item_name: incoming.item_name,
          qty: 1,
          rate: incoming.price_list_rate || incoming.rate || 0,
          amount: 0,
          discount_percentage: 0,
          note: '',
          uom: incoming.uom || incoming.stock_uom || 'Nos',
          stock_uom: incoming.stock_uom || 'Nos',
          item_image: incoming.item_image || null,
        };
        calcAmount(newItem);
        state.items.push(newItem);
      }
      state.quote = null;
    },

    /** Remove an item by item_code. */
    removeItem(state, action) {
      state.items = state.items.filter((i) => i.item_code !== action.payload);
      state.quote = null;
    },

    /**
     * Set the qty for an item. Removes the item if qty <= 0.
     * Payload: { item_code, qty }
     */
    updateQty(state, action) {
      const { item_code, qty } = action.payload;
      if (qty <= 0) {
        state.items = state.items.filter((i) => i.item_code !== item_code);
        state.quote = null;
        return;
      }
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.qty = qty;
        calcAmount(item);
        state.quote = null;
      }
    },

    /**
     * Set discount percentage for a line item.
     * Payload: { item_code, discount_percentage }
     */
    setLineDiscount(state, action) {
      const { item_code, discount_percentage } = action.payload;
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.discount_percentage = Math.min(100, Math.max(0, discount_percentage || 0));
        calcAmount(item);
        state.quote = null;
      }
    },

    /**
     * Set note for a line item.
     * Payload: { item_code, note }
     */
    setLineNote(state, action) {
      const { item_code, note } = action.payload;
      const item = state.items.find((i) => i.item_code === item_code);
      if (item) {
        item.note = note || '';
      }
    },

    /** Set or clear the customer. */
    setCustomer(state, action) {
      state.customer = action.payload;
      state.quote = null;
      state.loyaltyRedemption = initialState.loyaltyRedemption;
    },

    /** Store server-authoritative quote response. */
    setQuote(state, action) {
      state.quote = action.payload || null;
    },

    /** Clear quote when local cart/customer state is no longer quoteable. */
    clearQuote(state) {
      state.quote = null;
    },

    /** Set loyalty redemption options used by quote and checkout. */
    setLoyaltyRedemption(state, action) {
      state.loyaltyRedemption = {
        ...state.loyaltyRedemption,
        ...(action.payload || {}),
      };
      state.quote = null;
    },

    /** Reset the cart. */
    clearCart() {
      return initialState;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQty,
  setLineDiscount,
  setLineNote,
  setCustomer,
  setQuote,
  clearQuote,
  setLoyaltyRedemption,
  clearCart,
} = cartSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCartItems = (state) => state.cart.items;
export const selectCustomer = (state) => state.cart.customer;
export const selectQuote = (state) => state.cart.quote;
export const selectLoyaltyRedemption = (state) => state.cart.loyaltyRedemption;
export const selectAppliedPromotions = createSelector(
  selectQuote,
  (quote) => quote?.applied_pricing_rules || [],
);

export const selectCartTotals = createSelector(
  selectCartItems,
  (items) => ({
    subtotal: items.reduce((sum, i) => sum + i.amount, 0),
    itemCount: items.reduce((sum, i) => sum + i.qty, 0),
  }),
);

export const selectQuotedTotals = createSelector(
  selectCartTotals,
  selectQuote,
  (localTotals, quote) => ({
    ...localTotals,
    total: quote?.total ?? localTotals.subtotal,
    netTotal: quote?.net_total ?? localTotals.subtotal,
    grandTotal: quote?.grand_total ?? localTotals.subtotal,
    roundedTotal: quote?.rounded_total ?? quote?.grand_total ?? localTotals.subtotal,
    currency: quote?.currency,
    taxes: quote?.taxes || [],
    discountAmount: quote?.discount_amount || 0,
    loyaltyAmount: quote?.loyalty?.loyalty_amount || 0,
  }),
);

export default cartSlice.reducer;
